/**
 * Shared visual picture chooser.
 *
 * Every question editor uses this dialog. A teacher can search by filename or
 * tag, narrow the library to a familiar classroom category, and enlarge the
 * thumbnails without changing the picture's printed size.
 */

import { useMemo, useState } from 'react'
import type { Image } from '../../lib/api/client'
import { Button, Dialog, Input } from '../../components/ui'
import { Icon } from '../../components/icons'
import { Picture } from '../../components/Picture'
import { PictureGenerator } from './PictureGenerator'

type ThumbnailSize = 'small' | 'big' | 'extra'

type Category = {
  id: string
  label: string
  icon: keyof typeof Icon
  tags: string[]
}

const CATEGORIES: Category[] = [
  { id: 'animals', label: 'Animals', icon: 'paw', tags: ['animal', 'animals'] },
  {
    id: 'nature',
    label: 'Flowers & nature',
    icon: 'flower',
    tags: ['flower', 'flowers', 'nature', 'plant', 'plants', 'tree'],
  },
  {
    id: 'things',
    label: 'Things',
    icon: 'toy',
    tags: ['object', 'objects', 'thing', 'things', 'toy', 'food', 'fruit', 'school', 'classroom'],
  },
  // Kept in step with the tag vocabulary in app/ai/tagging.py, which is what
  // files a newly generated picture under one of these headings.
  {
    id: 'shapes',
    label: 'Shapes',
    icon: 'shapes',
    tags: ['shape', 'shapes', 'geometry', 'circle', 'corners'],
  },
  {
    id: 'vehicles',
    label: 'Vehicles',
    icon: 'car',
    tags: ['vehicle', 'vehicles', 'transport'],
  },
  {
    id: 'coloring',
    label: 'Colouring',
    icon: 'pencil',
    tags: ['coloring', 'colouring', 'outline'],
  },
]

export function PicturePicker({
  library,
  chosen,
  disabled,
  onPick,
}: {
  library: Image[]
  chosen: string | null
  disabled?: boolean
  onPick: (imageId: string | null) => void
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [category, setCategory] = useState('all')
  const [thumbnailSize, setThumbnailSize] = useState<ThumbnailSize>('big')
  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase()
    return library.filter((image) => {
      const tags = normalisedTags(image)
      const matchesQuery =
        !needle ||
        image.original_filename.toLowerCase().includes(needle) ||
        tags.some((tag) => tag.includes(needle))
      const matchesCategory = imageMatchesCategory(image, category)
      return matchesQuery && matchesCategory
    })
  }, [category, library, query])

  const groups = useMemo(() => {
    if (category !== 'all') return [{ id: category, label: '', images: visible }]

    const grouped = new Map<string, { id: string; label: string; images: Image[] }>()
    for (const image of visible) {
      const item = categoryFor(image)
      const current = grouped.get(item.id) ?? { id: item.id, label: item.label, images: [] }
      current.images.push(image)
      grouped.set(item.id, current)
    }
    return [...grouped.values()]
  }, [category, visible])

  if (disabled) return <span />

  const choose = (imageId: string | null) => {
    onPick(imageId)
    setOpen(false)
  }

  return (
    <>
      <button
        type="button"
        className="tc-btn tc-btn-secondary tc-btn-md tc-picture-picker-trigger"
        aria-label={chosen ? 'Change picture' : 'Choose picture'}
        onClick={() => setOpen(true)}
      >
        {chosen ? (
          <Picture className="tc-option-thumb" id={chosen} />
        ) : (
          <span className="tc-picker-empty">Picture</span>
        )}
      </button>

      <Dialog
        open={open}
        title={
          <span className="tc-picture-library-title">
            <Icon.pictures size={22} />
            Choose a picture
          </span>
        }
        onClose={() => setOpen(false)}
        className="tc-picture-library-dialog"
      >
        <div className="tc-picture-generate">
          <PictureGenerator
            library={library}
            onUse={(imageId) => {
              onPick(imageId)
              setOpen(false)
            }}
          />
        </div>

        {library.length === 0 ? (
          <div className="tc-picture-library-empty">
            <Icon.pictures size={32} />
            <strong>No pictures yet</strong>
            <span>Add pictures under Pictures, then return here to choose one.</span>
          </div>
        ) : (
          <>
            <div className="tc-picture-library-toolbar">
              <label className="tc-picture-library-search">
                <span className="tc-sr-only">Search pictures</span>
                <span aria-hidden="true">
                  <Icon.search size={17} />
                </span>
                <Input
                  value={query}
                  placeholder="Search animals, flowers, shapes…"
                  onChange={(event) => setQuery(event.target.value)}
                />
              </label>

              <div className="tc-picture-size-control" aria-label="Thumbnail size">
                <span>View</span>
                {(
                  [
                    ['small', 'Small'],
                    ['big', 'Big'],
                    ['extra', 'Extra big'],
                  ] as const
                ).map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    className={thumbnailSize === value ? 'is-active' : ''}
                    aria-pressed={thumbnailSize === value}
                    onClick={() => setThumbnailSize(value)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div className="tc-picture-library-layout">
              <nav className="tc-picture-library-categories" aria-label="Picture categories">
                <CategoryButton
                  active={category === 'all'}
                  icon="grid"
                  label="All pictures"
                  count={library.length}
                  onClick={() => setCategory('all')}
                />
                {CATEGORIES.map((item) => {
                  const count = library.filter((image) => imageMatchesCategory(image, item.id)).length
                  if (!count) return null
                  return (
                    <CategoryButton
                      key={item.id}
                      active={category === item.id}
                      icon={item.icon}
                      label={item.label}
                      count={count}
                      onClick={() => setCategory(item.id)}
                    />
                  )
                })}
                {library.some((image) => image.source !== 'starter') && (
                  <CategoryButton
                    active={category === 'mine'}
                    icon="plus"
                    label="My pictures"
                    count={library.filter((image) => image.source !== 'starter').length}
                    onClick={() => setCategory('mine')}
                  />
                )}
              </nav>

              <div className="tc-picture-library-results">
                <div className="tc-picture-library-summary">
                  <span>
                    {visible.length} {visible.length === 1 ? 'picture' : 'pictures'}
                  </span>
                  {chosen && (
                    <Button size="sm" variant="danger" onClick={() => choose(null)}>
                      Remove current picture
                    </Button>
                  )}
                </div>

                {visible.length === 0 ? (
                  <div className="tc-picture-library-empty">
                    <strong>No matching pictures</strong>
                    <span>Try another word or category.</span>
                  </div>
                ) : (
                  groups.map((group) => (
                    <section key={group.id} className="tc-picture-library-group">
                      {group.label && <h3>{group.label}</h3>}
                      <div className={`tc-picker-grid is-${thumbnailSize}`}>
                        {group.images.map((image) => (
                          <button
                            key={image.id}
                            type="button"
                            className={`tc-picture-card ${image.id === chosen ? 'is-chosen' : ''}`}
                            title={friendlyName(image.original_filename)}
                            aria-pressed={image.id === chosen}
                            onClick={() => choose(image.id)}
                          >
                            <span className="tc-picture-card-image">
                              <Picture id={image.id} alt={friendlyName(image.original_filename)} />
                            </span>
                            <span className="tc-picture-card-name">
                              {friendlyName(image.original_filename)}
                            </span>
                            {normalisedTags(image).includes('coloring') && (
                              <span className="tc-picture-card-badge">Colouring</span>
                            )}
                          </button>
                        ))}
                      </div>
                    </section>
                  ))
                )}
              </div>
            </div>
          </>
        )}
      </Dialog>
    </>
  )
}

function CategoryButton({
  active,
  icon,
  label,
  count,
  onClick,
}: {
  active: boolean
  icon: keyof typeof Icon
  label: string
  count: number
  onClick: () => void
}) {
  return (
    <button type="button" className={active ? 'is-active' : ''} onClick={onClick}>
      <span aria-hidden="true">{(() => { const Glyph = Icon[icon]; return <Glyph size={17} /> })()}</span>
      <span>{label}</span>
      <small>{count}</small>
    </button>
  )
}

function normalisedTags(image: Image): string[] {
  return (image.tags ?? []).map((tag) => tag.toLowerCase())
}

function categoryFor(image: Image): { id: string; label: string } {
  if (image.source !== 'starter') return { id: 'mine', label: 'My pictures' }
  const tags = normalisedTags(image)

  // Colouring outlines live together even though they also describe an animal or
  // a shape. The other category filters still find them through their tags.
  if (tags.some((tag) => ['coloring', 'colouring', 'outline'].includes(tag))) {
    return { id: 'coloring', label: 'Colouring outlines' }
  }
  const category = CATEGORIES.find((item) => item.tags.some((tag) => tags.includes(tag)))
  return category ?? { id: 'other', label: 'Other pictures' }
}

function imageMatchesCategory(image: Image, category: string): boolean {
  if (category === 'all') return true
  if (category === 'mine') return image.source !== 'starter'

  const definition = CATEGORIES.find((item) => item.id === category)
  const tags = normalisedTags(image)
  return (
    categoryFor(image).id === category ||
    Boolean(definition?.tags.some((tag) => tags.includes(tag)))
  )
}

function friendlyName(filename: string): string {
  const withoutExtension = filename.replace(/\.[^.]+$/, '')
  const withoutPrefix = withoutExtension.replace(/^(starter|coloring)-/, '')
  return withoutPrefix
    .replace(/[-_]+/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase())
}
